/**
 * Mis conexiones — vivía en Configuración → Conexiones.
 *
 * Son las cuentas PERSONALES de quien está usando la app (su Google, su
 * Facebook), no las del negocio: si un compañero conecta la suya, no ve ni usa
 * ésta. Eso es lo primero que hay que dejar claro en pantalla, porque el
 * parecido con el registro de la app en Super Admin confunde a todo el mundo.
 */
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { KeyRound, Link2, Unlink } from 'lucide-react';

const PROVEEDORES = [
  { key: 'google', label: 'Google Calendar', desc: 'Sincroniza tus citas y respeta tu ocupado al agendar con IA' },
  { key: 'outlook', label: 'Outlook Calendar', desc: 'Microsoft 365 / Outlook: citas y huecos ocupados para la IA' },
  { key: 'meta', label: 'Meta', desc: 'Facebook (Fan Page, Ads)' },
  { key: 'instagram', label: 'Instagram', desc: 'Mensajes directos, comentarios y publicaciones' },
];

export default function BusinessConnections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conexiones, setConexiones] = useState({ google: null, outlook: null, meta: null, instagram: null });
  const [conectando, setConectando] = useState(null);

  const cargar = useCallback(() => {
    Promise.all(PROVEEDORES.map(p =>
      api.get(`/tenant/oauth/${p.key}/status`).catch(() => ({ data: { connected: false } }))
    )).then(rs => setConexiones(Object.fromEntries(PROVEEDORES.map((p, i) => [p.key, rs[i].data]))));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  // Al volver del proveedor, la URL trae el resultado. Se avisa y se limpia,
  // para que un refresco no repita el mensaje.
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    if (!oauth) return;
    const ok = searchParams.get('connected') === '1';
    const nombre = PROVEEDORES.find(p => p.key === oauth)?.label || oauth;
    toast[ok ? 'success' : 'error'](ok ? `${nombre} conectado` : `No se pudo conectar ${nombre}`);
    cargar();
    const siguiente = new URLSearchParams(searchParams);
    ['oauth', 'connected', 'reason'].forEach(k => siguiente.delete(k));
    setSearchParams(siguiente, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conectar = async (proveedor) => {
    setConectando(proveedor);
    try {
      // El return_to apunta aquí, a Mi Negocio — antes volvía a Configuración,
      // que ya no existe como menú.
      const { data } = await api.get(`/tenant/oauth/${proveedor}/authorize`,
        { params: { return_to: '/tenant/business/connections' } });
      window.location.href = data.authorize_url;
    } catch (e) {
      const detalle = e.response?.data?.detail;
      toast.error((typeof detalle === 'object' ? detalle?.detail : detalle)
        || `No se pudo iniciar la conexión con ${proveedor}`);
      setConectando(null);
    }
  };

  const desconectar = async (proveedor) => {
    const nombre = PROVEEDORES.find(p => p.key === proveedor)?.label || proveedor;
    if (!window.confirm(`¿Desconectar tu cuenta de ${nombre}?`)) return;
    try {
      await api.post(`/tenant/oauth/${proveedor}/disconnect`);
      toast.success('Cuenta desconectada');
      cargar();
    } catch { toast.error('No se pudo desconectar'); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="business-connections">
      <div className="flex items-center gap-3 mb-2">
        <KeyRound className="w-5 h-5 text-[#F72585]" />
        <h3 className="text-lg font-semibold text-[#0F172A]">Mis conexiones</h3>
      </div>
      <p className="text-sm text-[#64748B] mb-6">
        Conecta tus propias cuentas de Google o Meta — son personales tuyas, no del negocio.
        Un compañero que conecte la suya no ve ni usa la tuya.
      </p>

      <div className="space-y-3">
        {PROVEEDORES.map(p => {
          const con = conexiones[p.key];
          return (
            <div key={p.key} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg p-4" data-testid={`settings-connection-${p.key}`}>
              <div>
                <p className="text-sm font-medium text-[#0F172A]">{p.label}</p>
                <p className="text-xs text-[#64748B]">{p.desc}</p>
                {con?.connected && (
                  <p className="text-xs text-emerald-600 mt-0.5">Conectado{con.email ? ` — ${con.email}` : ''}</p>
                )}
              </div>
              {con?.connected ? (
                <Button size="sm" variant="outline" onClick={() => desconectar(p.key)}>
                  <Unlink className="w-3.5 h-3.5 mr-1.5" /> Desconectar
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => conectar(p.key)} disabled={conectando === p.key}>
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> {conectando === p.key ? 'Conectando...' : 'Conectar'}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
